import React, { useEffect, useMemo, useRef, useState } from "react";
import { Formik, Form, useFormikContext } from "formik";
import * as Yup from "yup";
import { useDispatch, useSelector } from "react-redux";
import { SecureField, maskName, maskEmail, maskPan, maskPassport, maskDob, maskMobile } from "./maskUtils";
import {
  PfInput,
  PfMasked,
  PfDate,
  PfGender,
  MASKS,
  isAtLeastAge,
} from "./ProposalFormFields";
import API from "../utils/API";
import { updateMastersData } from "../redux/slices/MastersDataSlice";
import { updateTravellerDetails } from "../redux/slices/TravellerDetailsSlice";
import { getDobFromAge } from "../TravelNewVersion/utility/utilityFunctions";

const hasFormikErrors = (errors) => {
  if (!errors) return false;
  if (typeof errors === "string") return errors.length > 0;
  if (Array.isArray(errors)) return errors.some(hasFormikErrors);
  if (typeof errors === "object")
    return Object.values(errors).some(hasFormikErrors);
  return false;
};

const FormikSync = ({ onValidityChange, onTravellersChange }) => {
  const { values, validateForm } = useFormikContext();
  const validationSeqRef = useRef(0);
  const travellersChangeRef = useRef(onTravellersChange);

  useEffect(() => {
    travellersChangeRef.current = onTravellersChange;
  }, [onTravellersChange]);

  useEffect(() => {
    const seq = ++validationSeqRef.current;
    validateForm().then((errors) => {
      if (validationSeqRef.current !== seq) return;
      onValidityChange?.(!hasFormikErrors(errors));
    });
  }, [values, validateForm, onValidityChange]);

  useEffect(() => {
    travellersChangeRef.current?.(values?.travellers || []);
  }, [values?.travellers]);

  return null;
};

const FormikHydrator = ({ occupationOptions, relationOptions }) => {
  const { values, setFieldValue } = useFormikContext();

  useEffect(() => {
    const traveller = values?.travellers?.[0];
    if (!traveller) return;

    const nextOccupation = normalizeSelectValue(
      traveller.occupation,
      occupationOptions,
    );
    const nextRelation = normalizeSelectValue(
      traveller.trv_nomineerelation,
      relationOptions,
    );
    if (nextOccupation !== traveller.occupation) {
      setFieldValue("travellers[0].occupation", nextOccupation, false);
    }
    if (nextRelation !== traveller.trv_nomineerelation) {
      setFieldValue("travellers[0].trv_nomineerelation", nextRelation, false);
    }
  }, [occupationOptions, relationOptions, setFieldValue, values?.travellers]);

  return null;
};

const loadedMasterInsurerIds = new Set();
const EMAIL_MAX_LENGTH = 100;
const emailPattern =
  /^[A-Za-z0-9._%+-]+@[A-Za-z0-9-]+(?:\.[A-Za-z0-9-]+)*\.[A-Za-z]{2,5}$/;

const normalizeSpaces = (value) =>
  String(value || "")
    .replace(/\s+/g, " ")
    .replace(/^\s+/, "");

const toTitleCase = (value) =>
  normalizeSpaces(value)
    .toLowerCase()
    .replace(/\b[a-z]/g, (ch) => ch.toUpperCase());

const fullNamePattern = /^[A-Za-z]{2,}(?:\s+[A-Za-z]{1,})+$/;
const SALUTATION_OPTIONS = [
  { label: "Mr", value: "Mr" },
  { label: "Mrs", value: "Mrs" },
  { label: "Ms", value: "Ms" },
];

const sanitizeEmailInput = (value) =>
  String(value || "")
    .replace(/[^A-Za-z0-9._%+\-@]/g, "")
    .replace(/\s+/g, "")
    .replace(/@{2,}/g, "@");

const limitEmailDomainSuffix = (value) => {
  const raw = sanitizeEmailInput(value);
  const atIndex = raw.lastIndexOf("@");
  if (atIndex < 0) return raw;

  const localPart = raw.slice(0, atIndex + 1);
  const domainPart = raw.slice(atIndex + 1);
  const lastDotIndex = domainPart.lastIndexOf(".");
  if (lastDotIndex < 0) return `${localPart}${domainPart}`;

  const domainHead = domainPart.slice(0, lastDotIndex + 1);
  const domainTail = domainPart.slice(lastDotIndex + 1, lastDotIndex + 6);
  return `${localPart}${domainHead}${domainTail}`;
};

const stripRemovedTravellerFields = (row = {}) => {
  const rest = { ...row };
  delete rest.maritalstatus;
  delete rest.education;
  delete rest.annualincome;
  delete rest.visaType;
  return rest;
};

const parseDobToAge = (value) => {
  const text = String(value || "").trim();
  if (!text) return "";

  const direct = new Date(text);
  if (!Number.isNaN(direct.getTime())) {
    const today = new Date();
    let age = today.getFullYear() - direct.getFullYear();
    const monthDiff = today.getMonth() - direct.getMonth();
    if (
      monthDiff < 0 ||
      (monthDiff === 0 && today.getDate() < direct.getDate())
    ) {
      age -= 1;
    }
    return age >= 0 ? String(age) : "";
  }

  const dmy = text.match(/^(\d{2})-(\d{2})-(\d{4})$/);
  if (dmy) {
    const [_, dd, mm, yyyy] = dmy;
    const parsed = new Date(`${yyyy}-${mm}-${dd}T00:00:00`);
    if (!Number.isNaN(parsed.getTime())) {
      const today = new Date();
      let age = today.getFullYear() - parsed.getFullYear();
      const monthDiff = today.getMonth() - parsed.getMonth();
      if (
        monthDiff < 0 ||
        (monthDiff === 0 && today.getDate() < parsed.getDate())
      ) {
        age -= 1;
      }
      return age >= 0 ? String(age) : "";
    }
  }

  return "";
};

const normalizeSelectValue = (savedValue, options) => {
  if (savedValue && typeof savedValue === "object") {
    const code =
      savedValue?.Code ||
      savedValue?.code ||
      savedValue?.RelationID ||
      savedValue?.RelationId ||
      savedValue?.value ||
      "";
    const label =
      savedValue?.MasterName ||
      savedValue?.RelationName ||
      savedValue?.label ||
      "";
    if (code && label) {
      return `${code}~${label}`;
    }
    if (code) {
      const matchByCode = Array.isArray(options)
        ? options.find(
            (opt) =>
              String(opt?.Code ?? opt?.RelationID ?? "").trim() ===
              String(code).trim(),
          )
        : null;
      if (matchByCode) {
        const nextCode = matchByCode?.Code ?? matchByCode?.RelationID ?? code;
        const nextLabel =
          matchByCode?.MasterName ?? matchByCode?.RelationName ?? label;
        return nextCode && nextLabel
          ? `${nextCode}~${nextLabel}`
          : String(code);
      }
      return String(code);
    }
    if (label) {
      const matchByLabel = Array.isArray(options)
        ? options.find(
            (opt) =>
              String(opt?.MasterName ?? opt?.RelationName ?? opt?.label ?? "")
                .trim()
                .toLowerCase() === String(label).trim().toLowerCase(),
          )
        : null;
      if (matchByLabel) {
        const nextCode = matchByLabel?.Code ?? matchByLabel?.RelationID ?? "";
        const nextLabel =
          matchByLabel?.MasterName ?? matchByLabel?.RelationName ?? label;
        return nextCode && nextLabel
          ? `${nextCode}~${nextLabel}`
          : String(label);
      }
      return String(label);
    }
  }

  const rawValue = savedValue;
  const raw = String(rawValue || "").trim();
  if (!raw) return "";
  if (raw.includes("~")) return raw;
  const match = Array.isArray(options)
    ? options.find(
        (opt) =>
          String(opt?.Code ?? opt?.RelationID ?? "").trim() === raw ||
          String(opt?.MasterName ?? opt?.RelationName ?? opt?.label ?? "")
            .trim()
            .toLowerCase() === raw.toLowerCase(),
      )
    : null;
  if (!match) return raw;
  const code = match?.Code ?? match?.RelationID ?? "";
  const label = match?.MasterName ?? match?.RelationName ?? "";
  return code ? `${code}~${label}` : raw;
};

const CustomSelect = ({
  label,
  required,
  error,
  value,
  placeholder = "Select",
  options = [],
  onChange,
  onBlur,
}) => {
  const [open, setOpen] = useState(false);
  const wrapRef = useRef(null);

  const selectedLabel = useMemo(() => {
    const match = options.find((opt) => String(opt.value) === String(value));
    return match?.label || placeholder;
  }, [options, value, placeholder]);

  useEffect(() => {
    if (!open) return undefined;
    const handleDown = (e) => {
      if (wrapRef.current && !wrapRef.current.contains(e.target)) {
        setOpen(false);
        onBlur?.();
      }
    };
    document.addEventListener("mousedown", handleDown);
    return () => document.removeEventListener("mousedown", handleDown);
  }, [open, onBlur]);

  return (
    <div
      className={`pf-field${open ? " pf-field--open" : ""}`}
      ref={wrapRef}
      style={{ position: "relative" }}
    >
      {label && (
        <label className="pf-label">
          {label}
          {required && <span className="pf-required">*</span>}
        </label>
      )}
      <button
        type="button"
        className={`pf-select pf-select--custom${error ? " has-error" : ""}${
          value ? " has-value" : ""
        }${open ? " is-open" : ""}`}
        aria-expanded={open}
        aria-haspopup="listbox"
        onClick={() => setOpen((prev) => !prev)}
      >
        <span className="pf-select-value">{selectedLabel}</span>
        <span className={`pf-select-caret${open ? " is-open" : ""}`}>▾</span>
      </button>
      {open && (
        <div className="pf-select-menu" role="listbox">
          {options.map((opt) => (
            <button
              key={opt.value}
              type="button"
              className={`pf-select-item${
                String(opt.value) === String(value) ? " is-selected" : ""
              }`}
              onClick={() => {
                onChange?.(opt.value);
                setOpen(false);
              }}
            >
              {opt.label}
            </button>
          ))}
        </div>
      )}
      {error && <span className="pf-error">⚠ {error}</span>}
    </div>
  );
};

const schema = Yup.object({
  travellers: Yup.array().of(
    Yup.object({
      fullName: Yup.string()
        .matches(fullNamePattern, "Enter at least first and last name")
        .required("Full name is required"),
        salutation: Yup.string().required("Salutation is required"),
        mobileNo: Yup.string()
            .matches(/^[6-9]\d{9}$/, "Enter valid mobile number")
            .required("Mobile number is required"),
      email: Yup.string()
        .trim()
        .max(EMAIL_MAX_LENGTH, "Email must be 100 characters or less")
        .matches(emailPattern, "Enter a valid email.")
        .required("Email is required"),
      gender: Yup.string()
        .oneOf(["Male", "Female"], "Select gender")
        .required("Gender is required"),
      dob: Yup.string()
        .required("Date of birth is required")
        .test("min-age", "Traveller must be at least 18 years old", (value) =>
          isAtLeastAge(value, 18),
        ),
      occupation: Yup.string()
        .notOneOf(["", "occupation"], "Select occupation")
        .required("Occupation is required"),
      passportNumber: Yup.string()
        .matches(/^[A-Za-z0-9]{6,20}$/, "Invalid passport number")
        .required("Passport number is required"),
      ProposerPAN: Yup.string()
        .matches(/^[A-Za-z]{5}[0-9]{4}[A-Za-z]{1}$/, "Invalid PAN format")
        .required("PAN number is required"),
      nomineeName: Yup.string()
        .matches(fullNamePattern, "Enter at least first and last name")
        .required("Nominee name is required"),
      trv_nomineerelation: Yup.string()
        .test(
          "valid-nominee-relation",
          "Select nominee relation",
          (value) => Boolean(value) && String(value).includes("~"),
        )
        .required("Select nominee relation"),
    }),
  ),
});

const defaultTraveller = (row = {}) => ({
  ...stripRemovedTravellerFields(row),
  salutation: row.salutation || "",
  fullName: row.fullName || "",
    email: row.email || "",
    mobileNo: row.mobileNo || row.MobileNo || "",
  gender: row.gender || "Male",
  dob: row.dob || getDobFromAge(row.age || row.Age || "") || "",
  age: row.age || row.Age || parseDobToAge(row.dob || row.DOB || ""),
  passportNumber: row.passportNumber || "",
  nomineeName: row.nomineeName || "",
  occupation: normalizeSelectValue(row.occupation || row.Occupation || "", []),
  trv_nomineerelation: normalizeSelectValue(
    row.trv_nomineerelation ||
      row.relation ||
      row.nomineeRelation ||
      row.NomineeRelation ||
      "",
    [],
  ),
  Relation: row.relation || "",
  ProposerPAN: row.ProposerPAN || "",
});

const Travellerdetailform = ({
  ProposalData,
  quoteData,
  travellersList,
  setTravellers,
  HandleFormValidation,
}) => {
  const dispatch = useDispatch();
  const savedTravellerDetails = useSelector((state) => state.TravellerDetails);
  const [Occupation, setOccupation] = useState([]);
  const [trv_nomineerelation, settrv_nomineerelation] = useState([]);
  console.log(savedTravellerDetails);

  const fetchMasterData = async (masterType, state) => {
    try {
      const model = {
        URL: "GetSmcMasters",
        PostString: JSON.stringify({
          CompanyID: quoteData.InsurerID,
          MasterType: masterType,
        }),
      };

      //new change 9/12/2026
      const Res = await API.CallRequestWithHeader(model);
      if (Res?.StatusCode === 200) {
        const next = Array.isArray(Res.Response) ? Res.Response : [];
        state(next);
        if (masterType === "nomineerelation") {
            dispatch(updateMastersData({ nomineeRelationList: next }));
        }
      }
    } catch (e) {
      return Promise.resolve([]);
    }
  };

  useEffect(() => {
    const insurerId = quoteData?.InsurerID ?? quoteData?.insurerID;
    if (!insurerId) return;

    // const cacheKey = String(insurerId);
    // if (loadedMasterInsurerIds.has(cacheKey)) return;
    // loadedMasterInsurerIds.add(cacheKey);

    fetchMasterData("Occupation", setOccupation);
    fetchMasterData("nomineerelation", settrv_nomineerelation);
  }, [quoteData?.InsurerID, quoteData?.insurerID]);

  /*const [initialValues] = useState(() => ({
    travellers: [
      defaultTraveller(
        savedTravellerDetails?.travellers?.[0] ||
          travellersList?.[0] ||
          ProposalData?.Travellers?.[0] ||
          {},
      ),
    ],
  }));*/

    const [initialValues] = useState(() => {
        const base = defaultTraveller(
            savedTravellerDetails?.travellers?.[0] ||
            travellersList?.[0] ||
            ProposalData?.Travellers?.[0] ||
            {},
        );
        return {
            travellers: [
                {
                    ...base,
                    mobileNo:
                        base.mobileNo ||
                        savedTravellerDetails?.mobileNo ||
                        ProposalData?.MobileNo ||
                        "",
                },
            ],
        };
    });

  const handleSubmitValues = (values) => {
    const sanitizedTravellers = (values.travellers || []).map(
      stripRemovedTravellerFields,
    );
    setTravellers(sanitizedTravellers);
    dispatch(
      updateTravellerDetails({
          ...savedTravellerDetails,
          mobileNo: sanitizedTravellers?.[0]?.mobileNo || savedTravellerDetails?.mobileNo || "",
        travellers: sanitizedTravellers,
      }),
    );
  };

  return (
    <Formik
      initialValues={initialValues}
      validationSchema={schema}
      validateOnMount
      validateOnChange
      validateOnBlur
      onSubmit={handleSubmitValues}
    >
      {({ values, errors, touched, setFieldValue, setFieldTouched }) => {
        const tErr = (field) => errors.travellers?.[0]?.[field];
        const tTch = (field) => touched.travellers?.[0]?.[field];
        const show = (field) => tErr(field) && tTch(field);

        const change = (field) => (e) => {
          const raw = typeof e === "string" ? e : e.target.value;
          const val =
            field === "email"
              ? limitEmailDomainSuffix(raw)
              : field === "fullName" || field === "nomineeName"
                ? toTitleCase(raw)
                : normalizeSpaces(raw);
          setFieldValue(`travellers[0].${field}`, val, true);
          if (field === "dob") {
            setFieldValue(
              "travellers[0].age",
              parseDobToAge(val),
              false,
            );
          }
        };

        const val = (field) => values.travellers[0]?.[field] ?? "";

        return (
          <Form>
            <FormikHydrator
              occupationOptions={Occupation}
              relationOptions={trv_nomineerelation}
            />
            <FormikSync
              onValidityChange={HandleFormValidation}
              onTravellersChange={setTravellers}
            />

            <div style={{ marginBottom: 0 }}>
              {/* <div className="pg-traveller-head">
                <span className="pg-traveller-badge">Traveller 1</span>
                <span className="pg-traveller-tag">Insured / Proposer</span>
              </div> */}

              <div className="pg-form-grid">
                <PfGender
                  label="Gender"
                  required
                  error={show("gender") ? tErr("gender") : ""}
                  value={val("gender")}
                  onChange={(v) => {
                    setFieldValue("travellers[0].gender", v, true);
                    setFieldTouched("travellers[0].gender", true, false);
                  }}
                />
                <CustomSelect
                  label="Salutation"
                  required
                  error={show("salutation") ? tErr("salutation") : ""}
                  value={val("salutation")}
                  placeholder="Select salutation"
                  options={[
                    { label: "Select salutation", value: "" },
                    ...SALUTATION_OPTIONS,
                  ]}
                  onChange={(v) => setFieldValue("travellers[0].salutation", v, true)}
                  onBlur={() =>
                    setFieldTouched("travellers[0].salutation", true)
                  }
                />
                {/*<PfMasked
                  label="Full Name"
                  required
                  mask={MASKS.name}
                  className="pf-field--single-line-error"
                  placeholder="Enter full name"
                  error={show("fullName") ? tErr("fullName") : ""}
                  value={val("fullName")}
                  onChange={change("fullName")}
                  onBlur={() => setFieldTouched("travellers[0].fullName", true)}
                />*/}
                        <SecureField
                            as={PfMasked}
                            maskFn={maskName}
                            label="Full Name"
                            required
                            mask={MASKS.name}
                            className="pf-field--single-line-error"
                            placeholder="Enter full name"
                            error={show("fullName") ? tErr("fullName") : ""}
                            value={val("fullName")}
                            onChange={change("fullName")}
                            onBlur={() => setFieldTouched("travellers[0].fullName", true)}
                        />

                {/*<PfInput
                  label="Email"
                  required
                  type="email"
                  inputMode="email"
                  autoComplete="email"
                  maxLength={EMAIL_MAX_LENGTH}
                  placeholder="Enter email address"
                  error={show("email") ? tErr("email") : ""}
                  value={val("email")}
                  onChange={change("email")}
                  onKeyDown={(e) => {
                    if (e.key === " ") e.preventDefault();
                  }}
                  onBlur={() => setFieldTouched("travellers[0].email", true)}
                />*/}
                        <SecureField
                            as={PfInput}
                            maskFn={maskEmail}
                            label="Email"
                            required
                            type="email"
                            inputMode="email"
                            autoComplete="email"
                            maxLength={EMAIL_MAX_LENGTH}
                            placeholder="Enter email address"
                            error={show("email") ? tErr("email") : ""}
                            value={val("email")}
                            onChange={change("email")}
                            onKeyDown={(e) => { if (e.key === " ") e.preventDefault(); }}
                            onBlur={() => setFieldTouched("travellers[0].email", true)}
                        />

                        <SecureField
                            as={PfMasked}
                            maskFn={maskMobile}
                            label="Mobile Number"
                            required
                            mask={MASKS.mobile}
                            guide={false}
                            showMask={false}
                            placeholder="Enter mobile number"
                            error={show("mobileNo") ? tErr("mobileNo") : ""}
                            value={val("mobileNo")}
                            onChange={change("mobileNo")}
                            onBlur={() => setFieldTouched("travellers[0].mobileNo", true)}
                        />

                {/*<PfDate
                  label="Date of Birth"
                  required
                  error={show("dob") ? tErr("dob") : ""}
                  value={val("dob")}
                  onChange={change("dob")}
                  onBlur={() => setFieldTouched("travellers[0].dob", true)}
                  minDate={new Date(new Date().getFullYear() - 100, 0, 1)}
                  maxDate={
                    new Date(
                      new Date().getFullYear() - 18,
                      new Date().getMonth(),
                      new Date().getDate(),
                    )
                  }
                  allowTyping
                />*/}

                        <SecureField
                            as={PfDate}
                            maskFn={maskDob}
                            label="Date of Birth"
                            required
                            error={show("dob") ? tErr("dob") : ""}
                            value={val("dob")}
                            onChange={change("dob")}
                            onBlur={() => setFieldTouched("travellers[0].dob", true)}
                            minDate={new Date(new Date().getFullYear() - 100, 0, 1)}
                            maxDate={
                                new Date(
                                    new Date().getFullYear() - 18,
                                    new Date().getMonth(),
                                    new Date().getDate(),
                                )
                            }
                            allowTyping
                        />

                <CustomSelect
                  label="Occupation"
                  required
                  error={show("occupation") ? tErr("occupation") : ""}
                  value={val("occupation")}
                  placeholder="Select occupation"
                  options={[
                    { label: "Select occupation", value: "" },
                    ...(Array.isArray(Occupation)
                      ? Occupation.map((o) => ({
                          label: o.MasterName,
                          value: `${o.Code}~${o.MasterName}`,
                        }))
                      : []),
                  ]}
                  onChange={change("occupation")}
                  onBlur={() =>
                    setFieldTouched("travellers[0].occupation", true)
                  }
                />

                {/*<PfMasked
                  label="Passport Number"
                  required
                  mask={MASKS.passport}
                  style={{ textTransform: "uppercase" }}
                  placeholder="A1234567"
                  error={show("passportNumber") ? tErr("passportNumber") : ""}
                  value={val("passportNumber")}
                  onChange={change("passportNumber")}
                  onBlur={() =>
                    setFieldTouched("travellers[0].passportNumber", true)
                  }
                />*/}

                        <SecureField
                            as={PfMasked}
                            maskFn={maskPassport}
                            label="Passport Number"
                            required
                            mask={MASKS.passport}
                            style={{ textTransform: "uppercase" }}
                            placeholder="A1234567"
                            error={show("passportNumber") ? tErr("passportNumber") : ""}
                            value={val("passportNumber")}
                            onChange={change("passportNumber")}
                            onBlur={() => setFieldTouched("travellers[0].passportNumber", true)}
                        />

                {/*<PfMasked
                  label="PAN Number"
                  required
                  mask={MASKS.pan}
                  style={{ textTransform: "uppercase" }}
                  placeholder="ABCDE1234F"
                  error={show("ProposerPAN") ? tErr("ProposerPAN") : ""}
                  value={val("ProposerPAN")}
                  onChange={change("ProposerPAN")}
                  onBlur={() =>
                    setFieldTouched("travellers[0].ProposerPAN", true)
                  }
                />*/}

                        <SecureField
                            as={PfMasked}
                            maskFn={maskPan}
                            label="PAN Number"
                            required
                            mask={MASKS.pan}
                            style={{ textTransform: "uppercase" }}
                            placeholder="ABCDE1234F"
                            error={show("ProposerPAN") ? tErr("ProposerPAN") : ""}
                            value={val("ProposerPAN")}
                            onChange={change("ProposerPAN")}
                            onBlur={() => setFieldTouched("travellers[0].ProposerPAN", true)}
                        />

              </div>

              <div className="pg-form-section-label">Nominee Details</div>
              <div className="pg-form-grid">
                {/*<PfMasked
                  label="Nominee Full Name"
                  required
                  mask={MASKS.name}
                  placeholder="Enter full name"
                  error={show("nomineeName") ? tErr("nomineeName") : ""}
                  value={val("nomineeName")}
                  onChange={change("nomineeName")}
                  onBlur={() =>
                    setFieldTouched("travellers[0].nomineeName", true)
                  }
                />*/}

                        <SecureField
                            as={PfMasked}
                            maskFn={maskName}
                            label="Nominee Full Name"
                            required
                            mask={MASKS.name}
                            placeholder="Enter full name"
                            error={show("nomineeName") ? tErr("nomineeName") : ""}
                            value={val("nomineeName")}
                            onChange={change("nomineeName")}
                            onBlur={() => setFieldTouched("travellers[0].nomineeName", true)}
                        />

                <CustomSelect
                  label="Nominee Relationship"
                  required
                  error={
                    show("trv_nomineerelation")
                      ? tErr("trv_nomineerelation")
                      : ""
                  }
                  value={val("trv_nomineerelation")}
                  placeholder="Select relation"
                  options={[
                    { label: "Select relation", value: "" },
                    ...(Array.isArray(trv_nomineerelation)
                      ? trv_nomineerelation.map((r) => ({
                          label: r.MasterName,
                          value: `${r.Code}~${r.MasterName}`,
                        }))
                      : []),
                  ]}
                  onChange={change("trv_nomineerelation")}
                  onBlur={() =>
                    setFieldTouched("travellers[0].trv_nomineerelation", true)
                  }
                />
              </div>
            </div>
          </Form>
        );
      }}
    </Formik>
  );
};

export default Travellerdetailform;
